import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import useAuthValidation from '../../hooks/useAuthValidation';
import useWebSocketConnection from '../../hooks/useWebSocketConnection';
import useResizablePanels from '../../hooks/useResizablePanels';
import useTheme from '../../hooks/useTheme';
import ResizeHandle from '../../components/ResizeHandle';
import TabControl from '../../components/TabControl';
import Markets from '../../components/Markets/Markets';
import TradeDesk from '../../components/TradeDesk/TradeDesk';
import './index.css';
import MarginPanel from '../../components/MarginPanel/MarginPanel';
import Positions from '../../components/Positions/Positions';
import Orders from '../../components/Orders/Orders';
import History from '../../components/History/History';
import { useWatchlist } from '../../contexts/WatchlistContext';
import TradingViewChart from '../../components/TradingViewChart/TradingViewChart';
import websocketService from '../../services/websocket';

const Dashboard = () => {
  // Use our custom hooks for authentication and WebSocket
  const { 
    loading: authLoading, 
    error: authError, 
    retry: retryAuth 
  } = useAuthValidation();
  
  const { 
    connectionStatus, 
    authStatus, 
    error: wsError,
    connect: connectWs,
    forceConnect: forceConnectWs,
    isLoading: wsLoading,
    reconnect
  } = useWebSocketConnection();
  
  // Use the watchlist context
  const { 
    watchlist, 
    fetchWatchlist, 
    isLoading: watchlistLoading 
  } = useWatchlist();

  // Track if we've already fetched the watchlist
  const initialWatchlistFetchedRef = useRef(false);
  
  // Use our custom hook for panel resizing
  const {
    handleMarketsResize,
    handleTradeDeskResize,
    handleTopRowResize,
    handleActivityResize,
    resetMarketsPanelWidth,
    currentTheme
  } = useResizablePanels();
  
  // Get theme from context
  const { theme, toggleTheme } = useTheme();

  // Track active tab in the Position/Order/History section
  const [activeTab, setActiveTab] = useState('positions');
  
  // Add state for selected market
  const [selectedMarket, setSelectedMarket] = useState(null);
  
  // Add separate state for live prices to prevent excessive re-renders
  const [livePrices, setLivePrices] = useState({});
  
  // Refs for panels
  const marketsRef = useRef(null);
  const marketsComponentRef = useRef(null);
  const tradeDeskRef = useRef(null);
  const graphRef = useRef(null);
  const activityRef = useRef(null);
  const marginRef = useRef(null);
  
  // State for refresh button
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // State for UI based on errors
  const [showAuthError, setShowAuthError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAuthError, setIsAuthError] = useState(false);
  const [showWsError, setShowWsError] = useState(false);

  const isConnected = connectionStatus === 'connected';
  const isAuthenticated = authStatus === 'authenticated';
  const error = isConnected ? null : wsError;

  // Add state for screen width tracking
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isCompatibleDevice, setIsCompatibleDevice] = useState(window.innerWidth >= 1250);

  // Function to handle refreshing the Markets component
  const handleRefreshMarkets = async () => {
    if (isRefreshing || !marketsComponentRef.current) return;
    
    setIsRefreshing(true);
    
    try {
      // First fetch watchlist data
      await fetchWatchlist();
      
      // Then refresh market data
      if (marketsComponentRef.current) {
        await marketsComponentRef.current.refresh();
      }
    } catch (error) {
      console.error('[Dashboard] Error refreshing markets data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch watchlist once when connection is established
  useEffect(() => {
    if (isConnected && isAuthenticated && !initialWatchlistFetchedRef.current) {
      console.log('[Dashboard] Initial watchlist fetch after authentication');
      // Set the flag before the async call to prevent race conditions
      initialWatchlistFetchedRef.current = true;
      
      // Fetch watchlist when connected and authenticated
      fetchWatchlist().catch(err => {
        console.error('[Dashboard] Error fetching watchlist:', err);
        // Reset flag on error so we can try again
        initialWatchlistFetchedRef.current = false;
      });
    } else if (!isConnected || !isAuthenticated) {
      // Reset the flag when disconnected so we fetch again on reconnection
      initialWatchlistFetchedRef.current = false;
    }
  }, [isConnected, isAuthenticated, fetchWatchlist]);

  // Attempt WebSocket connection on component mount
  useEffect(() => {
    // Check if we're being redirected due to auth failure
    const authRedirectTriggered = localStorage.getItem('auth_redirect_triggered');
    if (authRedirectTriggered) {
      console.log('[Dashboard] Auth redirect triggered - not attempting WebSocket connection');
      localStorage.removeItem('auth_redirect_triggered'); // Clear the flag
      // Also make sure we don't have any invalid tokens lingering
      if (localStorage.getItem('token')) {
        console.log('[Dashboard] Found token after auth redirect, this should not happen - clearing token');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        // Force redirect to login to ensure clean state
        window.location.href = '/login';
        return;
      }
      return;
    }
    
    // Check if max retries has been reached
    const maxRetriesReached = localStorage.getItem('ws_max_retries_reached');
    if (maxRetriesReached && connectionStatus === 'max_retries') {
      console.log('[Dashboard] Max retries reached - not attempting automatic WebSocket connection');
      return;
    }
    
    // If not connected and not loading auth, try to connect WebSocket
    // BUT only if not in an auth_failed state and no existing error
    if (connectionStatus !== 'connected' && 
        !authLoading && 
        authStatus !== 'auth_failed' && 
        !wsError) {
      console.log('[Dashboard] Attempting initial WebSocket connection');
      connectWs().catch(err => {
        console.error('[Dashboard] Initial WebSocket connection failed:', err);
      });
    } else if (authStatus === 'auth_failed' || (wsError && wsError.includes('authentication'))) {
      console.log('[Dashboard] Not attempting WebSocket connection due to authentication failure');
    }
  }, [connectionStatus, authLoading, connectWs, authStatus, wsError]);

  // Handle manual connection retry
  const handleManualConnect = async () => {
    console.log('[Dashboard] Manual WebSocket connection attempt');
    // Clear max retries flag when manually reconnecting
    localStorage.removeItem('ws_max_retries_reached');
    try {
      const reconnected = await reconnect();
      
      // If reconnection is successful, attempt to fetch watchlist
      if (reconnected && !initialWatchlistFetchedRef.current) {
        console.log('[Dashboard] Fetching watchlist after manual reconnection');
        initialWatchlistFetchedRef.current = true;
        await fetchWatchlist();
      }
    } catch (error) {
      console.error('[Dashboard] Manual WebSocket connection failed:', error);
      initialWatchlistFetchedRef.current = false;
    }
  };

  // Set UI state based on errors
  useEffect(() => {
    if (error) {
      // Check if error message indicates an authentication problem
      const authProblem = error.includes('authentication') || 
                         error.includes('token') || 
                         error.includes('log in') ||
                         error.includes('Connection rejected');
      
      setIsAuthError(authProblem);
      setErrorMessage(error);
      setShowAuthError(true);
    } else {
      setShowAuthError(false);
      setErrorMessage('');
    }
  }, [error]);

  // Handle WebSocket errors separately - don't block UI
  useEffect(() => {
    if (wsError && !wsError.includes('authentication')) {
      // Only set WebSocket errors that aren't authentication related
      setShowWsError(true);
      setErrorMessage(wsError);
      
      // Auto-hide WebSocket error after 5 seconds
      const timer = setTimeout(() => {
        setShowWsError(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else {
      setShowWsError(false);
    }
  }, [wsError]);

  // Inside the Dashboard component, just before the return statement
  // Add this useEffect for debugging

  useEffect(() => {
    if (selectedMarket) {
      console.log('Dashboard - Selected market:', selectedMarket);
    }
  }, [selectedMarket]);

  // Subscribe to price updates for the selected market
  useEffect(() => {
    if (!selectedMarket || !isConnected || !isAuthenticated) return;
    
    // First check for cached prices and use them if available
    const cachedPrice = websocketService.getCachedPrice(
      selectedMarket.originalId || selectedMarket.symbol
    );
    
    if (cachedPrice) {
      // Update live prices from cache immediately
      setLivePrices(prev => {
        const marketId = selectedMarket.originalId || selectedMarket.id;
        return {
          ...prev,
          [marketId]: {
            bidPrice: cachedPrice.bid !== null ? cachedPrice.bid : (prev[marketId]?.bidPrice || selectedMarket.bidPrice),
            askPrice: cachedPrice.ask !== null ? cachedPrice.ask : (prev[marketId]?.askPrice || selectedMarket.askPrice),
            price: cachedPrice.bid !== null && cachedPrice.ask !== null ? 
              (parseFloat(cachedPrice.bid) + parseFloat(cachedPrice.ask)) / 2 : 
              (cachedPrice.bid !== null ? cachedPrice.bid : 
               cachedPrice.ask !== null ? cachedPrice.ask : (prev[marketId]?.price || selectedMarket.price))
          }
        };
      });
    }
    
    // Subscribe to price-feed events for the selected market
    const handlePriceUpdate = (priceData) => {
      if (!priceData || !priceData.symbol) return;
      
      // Check if this update is for our selected market
      const isForSelectedMarket = 
        selectedMarket.originalId === priceData.symbol || 
        (selectedMarket.originalId && selectedMarket.originalId.includes(`.${priceData.symbol}`)) ||
        (selectedMarket.symbol === priceData.symbol);
      
      if (isForSelectedMarket) {
        // Update ONLY the live prices state instead of the entire market object
        setLivePrices(prev => {
          const marketId = selectedMarket.originalId || selectedMarket.id;
          return {
            ...prev,
            [marketId]: {
              bidPrice: priceData.bid !== null ? priceData.bid : (prev[marketId]?.bidPrice || selectedMarket.bidPrice),
              askPrice: priceData.ask !== null ? priceData.ask : (prev[marketId]?.askPrice || selectedMarket.askPrice),
              price: priceData.bid !== null && priceData.ask !== null ? 
                (parseFloat(priceData.bid) + parseFloat(priceData.ask)) / 2 : 
                (priceData.bid !== null ? priceData.bid : 
                 priceData.ask !== null ? priceData.ask : (prev[marketId]?.price || selectedMarket.price))
            }
          };
        });
      }
    };
    
    // Register for WebSocket price updates
    websocketService.on('price-feed', handlePriceUpdate);
    
    // Cleanup on unmount or when selected market changes
    return () => {
      websocketService.off('price-feed', handlePriceUpdate);
    };
  }, [selectedMarket, isConnected, isAuthenticated]);

  // Track screen width and set compatibility
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsCompatibleDevice(width >= 1250);
    };
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call handler right away to get initial state
    handleResize();
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Render loading state
  if (authLoading) {
    return (
      <div className={`dashboard-container ${theme}`}>
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
          <div className="loading-content">
            <h2>Validating authentication...</h2>
            <p>Please wait while we verify your login status.</p>
          </div>
        </div>
      </div>
    );
  }

  // Render device compatibility message for small screens
  if (!isCompatibleDevice) {
    return (
      <div className={`dashboard-container ${theme}`}>
        <div className="compatibility-error">
          <h2>Device Not Compatible</h2>
          <p>This application requires a screen width of at least 1250px.</p>
          <p>Please use a larger device or resize your browser window.</p>
          <div className="current-width">
            <span>Current width: {screenWidth}px</span>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (showAuthError) {
    return (
      <div className={`dashboard-container ${theme}`}>
        <div className="error-container">
          <h2>{isAuthError ? 'Authentication Error' : 'Connection Error'}</h2>
          <p>{errorMessage}</p>
          {isAuthError ? (
            <>
              <p className="auth-help-text">Your session may have expired or is invalid. Please log in again.</p>
              <div className="error-actions">
                <Link to="/login" className="primary-button">
                  Go to Login
                </Link>
                <button 
                  className="secondary-button"
                  onClick={() => {
                    // Use direct navigation as a fallback
                    window.location.href = '/login';
                  }}
                >
                  Force Login Page Reload
                </button>
              </div>
            </>
          ) : (
            <button onClick={retryAuth} className="retry-button">
              Retry Connection
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render main dashboard
  return (
    <div className={`dashboard-container ${theme}`}>
      {/* WebSocket Error Notification - only shows for non-auth WebSocket errors */}
      {showWsError && (
        <div className="ws-error-notification">
          <div className="notification-content">
            <span className="error-icon">⚠️</span>
            <p>{errorMessage}</p>
            <button 
              className="close-notification"
              onClick={() => setShowWsError(false)}
              title="Dismiss notification"
            >
              &times;
            </button>
          </div>
          <button onClick={handleManualConnect} className="retry-button-small">
            Retry Connection
          </button>
        </div>
      )}

      <div className="trading-grid">
        {/* Markets Panel */}
        <div className="trading-panel markets-panel" ref={marketsRef}>
          <div className="panel-header">
            <h3>Markets</h3>
            <div className="panel-header-actions">
              <button 
                className="reset-size-button" 
                onClick={resetMarketsPanelWidth}
                title="Reset panel to default width"
              >
                <span className="reset-icon">↔</span>
              </button>
              <button 
                className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
                onClick={handleRefreshMarkets}
                disabled={isRefreshing}
                title="Refresh market data"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                </svg>
              </button>
            </div>
          </div>
          <div className="panel-content">
            <Markets 
              ref={marketsComponentRef}
              onMarketSelect={(market) => {
                console.log('Selected market:', market);
                setSelectedMarket(market);
              }}
            />
          </div>
          <ResizeHandle onMouseDown={handleMarketsResize} direction="horizontal" />
        </div>
        
        {/* Right Side Container */}
        <div className="right-side">
          {/* TradeDesk Panel */}
          <div className="trading-panel tradedesk-panel" ref={tradeDeskRef}>
            <div className="panel-header">
              <h3>TradeDesk</h3>
            </div>
            <div className="panel-content">
              <TradeDesk 
                market={selectedMarket} 
                livePrices={livePrices}
              />
            </div>
            <ResizeHandle onMouseDown={handleTradeDeskResize} direction="horizontal" />
          </div>
          
          {/* Graph Panel */}
          <div className="trading-panel graph-panel" ref={graphRef}>
            <div className="panel-header">
              <h3>Graph</h3>
            </div>
            <div className="panel-content">
              <TradingViewChart 
                market={selectedMarket} 
                // Don't pass live prices to TradingViewChart to prevent re-renders
              />
            </div>
          </div>
          
          {/* Bottom Row Container */}
          <div className="bottom-row">
            {/* Activity Panel with Tabs */}
            <div className="trading-panel activity-panel" ref={activityRef}>
              <div className="panel-header">
                <h3>Activity</h3>
                <div className="tab-controls">
                  <TabControl 
                    label="Positions" 
                    active={activeTab === 'positions'} 
                    onClick={() => setActiveTab('positions')} 
                  />
                  <TabControl 
                    label="Orders" 
                    active={activeTab === 'orders'} 
                    onClick={() => setActiveTab('orders')} 
                  />
                  <TabControl 
                    label="History" 
                    active={activeTab === 'history'} 
                    onClick={() => setActiveTab('history')} 
                  />
                </div>
              </div>
              <div className="panel-content">
                {activeTab === 'positions' && (
                  <div className="positions-container">
                    <Positions />
                  </div>
                )}
                
                {activeTab === 'orders' && (
                  <div className="orders-container">
                    <Orders />
                  </div>
                )}
                
                {activeTab === 'history' && (
                  <div className="history-container">
                    <History />
                  </div>
                )}
              </div>
              <ResizeHandle onMouseDown={handleActivityResize} direction="horizontal" />
            </div>
            
            {/* Margin Panel */}
            <div className="trading-panel margin-panel" ref={marginRef}>
              <div className="panel-header">
                <h3>Margin</h3>
              </div>
              <div className="panel-content">
                <MarginPanel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 